<template>
  <div class="rule-actions">
    <label class="d-block mb-1">
      Automated Actions
      <i id="rule-actions-info" class="fa fa-info-circle text-muted ml-1" style="cursor:pointer;"></i>
      <b-tooltip target="rule-actions-info" placement="right" triggers="hover">
        Changes applied to the triggering device whenever this rule's condition holds.
        They run alongside notifications, not instead of them.
      </b-tooltip>
    </label>

    <b-alert v-if="!isAdmin" show variant="light" class="py-2 small mb-0">
      <i class="fa fa-lock mr-1"></i>
      Actions change device state, so only an admin can add or edit them.
      <span v-if="localActions.length">This rule has {{ localActions.length }}.</span>
    </b-alert>

    <template v-else>
      <b-table
        v-if="localActions.length"
        :items="localActions"
        :fields="fields"
        small
        striped
        class="mb-2"
      >
        <template #cell(type)="{ item }">
          <strong>{{ typeLabel(item.type) }}</strong>
          <div class="text-muted small">{{ item.type }}</div>
        </template>

        <template #cell(params)="{ item }">
          <code class="small">{{ describeParams(item) }}</code>
        </template>

        <template #cell(enabled)="{ item, index }">
          <b-form-checkbox
            :checked="item.enabled !== false"
            switch
            :disabled="busy"
            @change="setEnabled(index, $event)"
          ></b-form-checkbox>
        </template>

        <template #cell(actions)="{ index }">
          <b-button size="sm" variant="outline-secondary" class="mr-1" :disabled="busy" @click="startEdit(index)">
            <i class="fa fa-edit"></i>
          </b-button>
          <b-button size="sm" variant="outline-danger" :disabled="busy" @click="removeAction(index)">
            <i class="fa fa-trash"></i>
          </b-button>
        </template>
      </b-table>

      <p v-else class="text-muted small mb-2">
        No actions — this rule only sends notifications.
      </p>

      <!-- Add / edit form -->
      <b-card no-body class="p-2 bg-light">
        <b-row>
          <b-col md="5">
            <b-form-group label="Action" label-for="ra-type" class="mb-2">
              <b-form-select
                id="ra-type"
                v-model="draft.type"
                :options="typeOptions"
                size="sm"
                @change="onTypeChange"
              ></b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="7">
            <!-- Known action type: render its declared fields -->
            <template v-if="draftMeta">
              <b-form-group
                v-for="field in draftMeta.fields"
                :key="field.key"
                :label="field.label"
                :label-for="'ra-' + field.key"
                class="mb-2"
              >
                <b-form-select
                  v-if="field.type === 'select'"
                  :id="'ra-' + field.key"
                  v-model="draft.params[field.key]"
                  :options="field.options"
                  size="sm"
                ></b-form-select>
                <b-form-input
                  v-else
                  :id="'ra-' + field.key"
                  v-model="draft.params[field.key]"
                  size="sm"
                ></b-form-input>
              </b-form-group>
            </template>

            <!-- Unknown type the server supports but this UI has no form for -->
            <b-form-group v-else-if="draft.type" label="Params (JSON)" label-for="ra-json" class="mb-2">
              <b-form-textarea
                id="ra-json"
                v-model="draft.paramsJson"
                rows="2"
                size="sm"
                placeholder='{ "key": "value" }'
                :state="draft.paramsJson ? jsonValid : null"
              ></b-form-textarea>
              <b-form-invalid-feedback :state="draft.paramsJson ? jsonValid : null">
                Not valid JSON.
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <div v-if="draftMeta && draftMeta.description" class="text-muted small mb-2">
          <i class="fa fa-info-circle mr-1"></i>{{ draftMeta.description }}
        </div>

        <div v-if="draftMeta && draftMeta.warning" class="text-warning small mb-2">
          <i class="fa fa-exclamation-triangle mr-1"></i>{{ draftMeta.warning }}
        </div>

        <div class="d-flex justify-content-end">
          <b-button v-if="editIndex !== null" size="sm" variant="link" class="mr-1" @click="resetDraft">
            Cancel
          </b-button>
          <b-button size="sm" variant="primary" :disabled="!canSubmit || busy" @click="submitDraft">
            <b-spinner v-if="busy" small></b-spinner>
            <span v-else>{{ editIndex === null ? 'Add Action' : 'Update Action' }}</span>
          </b-button>
        </div>
      </b-card>

      <p v-if="!ruleId" class="text-muted small mt-1 mb-0">
        Actions are saved when the rule is created.
      </p>
    </template>
  </div>
</template>

<script>
import { getUser } from '@/lib/auth'
import {
  RULE_ACTION_TYPES_QUERY,
  CREATE_RULE_ACTION_MUTATION,
  UPDATE_RULE_ACTION_MUTATION,
  DELETE_RULE_ACTION_MUTATION
} from '@/constants/graphql'

// Per-type form descriptors. Any type the server reports that isn't listed here still
// works — it falls back to a raw JSON params editor — so a new server-side action type
// is usable before this map is updated.
const ACTION_TYPE_META = {
  setSleepType: {
    label: 'Set sleep mode',
    description: 'Writes the device config. The device picks it up on its next publish, ' +
      'so it works even while the device is asleep and unreachable.',
    warning: 'Pair this with a recovery rule on the opposite threshold, and leave a gap ' +
      'between the two. Overlapping thresholds make the config flip every evaluation.',
    fields: [
      {
        key: 'sleepType',
        label: 'Sleep mode',
        type: 'select',
        default: 'ulp',
        options: [
          { value: 'ulp', text: 'ULP — ultra low power, disconnects from network' },
          { value: 'stop', text: 'Stop / Network — stays connected, responsive commands' }
        ]
      }
    ]
  }
}

export default {
  name: 'RuleActions',
  props: {
    // Null while the rule does not exist yet — the parent flushes staged actions
    // after it has an id.
    ruleId: { type: String, default: null },
    actions: { type: Array, default () { return [] } }
  },
  data () {
    return {
      localActions: this.actions ? this.actions.map(function (a) { return Object.assign({}, a) }) : [],
      ruleActionTypes: [],
      busy: false,
      editIndex: null,
      draft: { type: '', params: {}, paramsJson: '' },
      fields: [
        { key: 'type', label: 'Action' },
        { key: 'params', label: 'Settings' },
        { key: 'enabled', label: 'On', thStyle: { width: '3.5rem' } },
        { key: 'actions', label: '', class: 'text-right', thStyle: { width: '6rem' } }
      ]
    }
  },
  computed: {
    isAdmin () {
      var user = getUser()
      return !!(user && user.role === 'ADMIN')
    },
    typeOptions () {
      // Prefer the server's registry; fall back to what this UI knows if the query
      // hasn't resolved (or the user cannot read it).
      var types = (this.ruleActionTypes && this.ruleActionTypes.length)
        ? this.ruleActionTypes
        : Object.keys(ACTION_TYPE_META)
      var self = this
      return [{ value: '', text: 'Select an action...' }].concat(
        types.map(function (t) { return { value: t, text: self.typeLabel(t) } })
      )
    },
    draftMeta () {
      return ACTION_TYPE_META[this.draft.type] || null
    },
    jsonValid () {
      if (!this.draft.paramsJson) return true
      try {
        var parsed = JSON.parse(this.draft.paramsJson)
        return !!parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      } catch (e) {
        return false
      }
    },
    canSubmit () {
      if (!this.draft.type) return false
      if (this.draftMeta) {
        return this.draftMeta.fields.every(function (f) {
          return this.draft.params[f.key] !== undefined && this.draft.params[f.key] !== ''
        }.bind(this))
      }
      return this.jsonValid
    }
  },
  watch: {
    actions (val) {
      this.localActions = (val || []).map(function (a) { return Object.assign({}, a) })
    }
  },
  methods: {
    typeLabel (type) {
      return (ACTION_TYPE_META[type] && ACTION_TYPE_META[type].label) || type
    },
    describeParams (action) {
      var params = action.params || {}
      var keys = Object.keys(params)
      if (!keys.length) return '—'
      return keys.map(function (k) { return k + ': ' + params[k] }).join(', ')
    },
    draftParams () {
      if (this.draftMeta) return Object.assign({}, this.draft.params)
      return this.draft.paramsJson ? JSON.parse(this.draft.paramsJson) : {}
    },
    onTypeChange (type) {
      var meta = ACTION_TYPE_META[type]
      var params = {}
      if (meta) {
        meta.fields.forEach(function (f) {
          if (f.default !== undefined) params[f.key] = f.default
        })
      }
      this.draft.params = params
      this.draft.paramsJson = ''
    },
    resetDraft () {
      this.editIndex = null
      this.draft = { type: '', params: {}, paramsJson: '' }
    },
    startEdit (index) {
      var action = this.localActions[index]
      this.editIndex = index
      this.draft.type = action.type
      if (ACTION_TYPE_META[action.type]) {
        this.draft.params = Object.assign({}, action.params || {})
        this.draft.paramsJson = ''
      } else {
        this.draft.params = {}
        this.draft.paramsJson = JSON.stringify(action.params || {}, null, 2)
      }
    },
    async submitDraft () {
      var params = this.draftParams()
      if (this.editIndex === null) {
        await this.addAction({ type: this.draft.type, params: params, enabled: true })
      } else {
        await this.applyUpdate(this.editIndex, { type: this.draft.type, params: params })
      }
      this.resetDraft()
    },
    async addAction (action) {
      if (!this.ruleId) {
        this.localActions.push(action)
        this.emitStaged()
        return
      }
      await this.run(async () => {
        var result = await this.$apollo.mutate({
          mutation: CREATE_RULE_ACTION_MUTATION,
          variables: { ruleId: this.ruleId, input: action }
        })
        this.localActions.push(result.data.createRuleAction)
        this.$emit('changed')
      })
    },
    async applyUpdate (index, patch) {
      var action = this.localActions[index]
      if (!this.ruleId || !action.id) {
        this.$set(this.localActions, index, Object.assign({}, action, patch))
        this.emitStaged()
        return
      }
      await this.run(async () => {
        var result = await this.$apollo.mutate({
          mutation: UPDATE_RULE_ACTION_MUTATION,
          variables: { id: action.id, input: patch }
        })
        this.$set(this.localActions, index, result.data.updateRuleAction)
        this.$emit('changed')
      })
    },
    setEnabled (index, value) {
      this.applyUpdate(index, { enabled: value })
    },
    async removeAction (index) {
      var action = this.localActions[index]
      if (!this.ruleId || !action.id) {
        this.localActions.splice(index, 1)
        this.emitStaged()
        return
      }
      await this.run(async () => {
        await this.$apollo.mutate({
          mutation: DELETE_RULE_ACTION_MUTATION,
          variables: { id: action.id }
        })
        this.localActions.splice(index, 1)
        this.$emit('changed')
      })
    },
    // Shared wrapper so a failed mutation surfaces instead of leaving the list out of
    // step with the server.
    async run (fn) {
      this.busy = true
      try {
        await fn()
      } catch (error) {
        this.$bvToast.toast(error.message || 'Action could not be saved.', {
          title: 'Error', variant: 'danger', solid: true
        })
      } finally {
        this.busy = false
      }
    },
    emitStaged () {
      this.$emit('input', this.localActions.slice())
    }
  },
  apollo: {
    ruleActionTypes: {
      query: RULE_ACTION_TYPES_QUERY,
      skip () { return !this.isAdmin },
      error () { /* non-fatal — fall back to the locally known types */ }
    }
  }
}
</script>

<style scoped>
.rule-actions >>> .table {
  margin-bottom: 0;
}
</style>
